import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/SiteSettingsContext';
// ThemeSelector removed - using PageStyleEditor instead
import { createApiUrl } from '../utils/config';

const Header = React.memo(() => {
  const { user, isAuthenticated, logout } = useAuth();
  const { getTotalItems } = useCart();
  const { isInitialized: themeInitialized, siteSettings } = useTheme();
  // eslint-disable-next-line no-unused-vars
  const [updateTrigger, forceUpdate] = useState({});
  const headerRef = useRef(null);
  const lastRenderStateRef = useRef({ themeInitialized: false, hasSiteSettings: false });
  const [headerLoadedOnce, setHeaderLoadedOnce] = useState(false); // Prevent header from disappearing after first load
  
  const [categories, setCategories] = useState([]);
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [showQuickHeader, setShowQuickHeader] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Mark header as loaded once siteSettings are available - use state to trigger re-render
  useEffect(() => {
    if (siteSettings && themeInitialized && !headerLoadedOnce) {
      setHeaderLoadedOnce(true);
    }
  }, [siteSettings, themeInitialized, headerLoadedOnce]);

  // Body padding disabled - MultiPageThemeManager controls all header styling
  // useEffect(() => {
  //   if (siteSettings?.header_sticky && headerRef.current) {
  //     const headerHeight = headerRef.current.offsetHeight;
  //     document.body.style.paddingTop = `${headerHeight}px`;
  //     return () => {
  //       document.body.style.paddingTop = '0';
  //     };
  //   }
  // }, [siteSettings?.header_sticky]);
  
  // Force update when theme colors change
  useEffect(() => {
    const handleThemeUpdate = () => {
      forceUpdate({});
    };
    
    window.addEventListener('themeColorUpdate', handleThemeUpdate);
    return () => window.removeEventListener('themeColorUpdate', handleThemeUpdate);
  }, []);

  // Force update when siteSettings change (especially header_height)
  useEffect(() => {
    if (siteSettings?.header_height) {
      console.log('🎯 Header height updated from siteSettings:', siteSettings.header_height);
      forceUpdate({});
    }
  }, [siteSettings?.header_height]);

  // Show basic header quickly while theme loads - OPTIMIZED
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!themeInitialized || !siteSettings) {
        const currentState = { themeInitialized, hasSiteSettings: !!siteSettings };
        const lastState = lastRenderStateRef.current;
        
        if (currentState.themeInitialized !== lastState.themeInitialized || 
            currentState.hasSiteSettings !== lastState.hasSiteSettings) {
          console.log('🔧 Header showing quick header fallback - theme:', themeInitialized, 'settings:', !!siteSettings);
          lastRenderStateRef.current = currentState;
        }
        
        setShowQuickHeader(true);
      } else {
        // siteSettings yüklendi, quick header'ı kapat
        setShowQuickHeader(false);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [themeInitialized, siteSettings]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(createApiUrl('api/categories'));
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false); // Close mobile menu on logout
  };

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Loading state DISABLED - was causing header to disappear on page navigation
  // Header will always show full navigation, using default values if siteSettings not loaded yet
  if (false) { // DISABLED: (!headerLoadedOnce && (!themeInitialized || (!siteSettings && !showQuickHeader)))
    if (!showQuickHeader) {
      return (
        <header className="header" style={{ 
          height: '150px', 
          minHeight: '150px', 
          display: 'flex', 
          alignItems: 'center',
          backgroundColor: 'var(--header-bg, #ffffff)',
          borderBottom: '1px solid var(--header-border, #e5e5e5)'
        }}>
          {/* Basic header styles to prevent collapse */}
          <style jsx="true">{`
            .header {
              background-color: var(--header-bg, #ffffff) !important;
              color: var(--header-text, #333) !important;
              border-bottom: 1px solid var(--header-border, #e5e5e5) !important;
              padding: 0 15px !important;
              min-height: 150px !important;
              height: 150px !important;
              display: flex !important;
              align-items: center !important;
              box-sizing: border-box !important;
            }
            
            .header > .container {
              height: 100% !important;
              max-width: ${siteSettings?.header_full_width ? '100%' : `${siteSettings?.header_max_width || 1400}px`} !important;
              margin: 0 auto !important;
              padding: 0 15px !important;
              display: flex !important;
              align-items: center !important;
            }
            
            .header .header-content {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              width: 100% !important;
            }
            
            .logo {
              margin: 0 !important;
              font-size: 24px !important;
            }
          `}</style>
          <div className="container">
            <div className="header-content">
              <h1 className="logo"></h1>
              <div className="loading-text">Loading...</div>
            </div>
          </div>
        </header>
      );
    }

    // Show basic functional header with mobile support and proper height
    return (
      <header 
        className="header"
        style={{ 
          height: '150px', 
          minHeight: '150px', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: 'var(--header-bg, #ffffff)',
          borderBottom: '1px solid var(--header-border, #e5e5e5)'
        }}
      >
        {/* Basic header styles to prevent collapse */}
        <style jsx="true">{`
          .header {
            background-color: var(--header-bg, #ffffff) !important;
            color: var(--header-text, #333) !important;
            border-bottom: 1px solid var(--header-border, #e5e5e5) !important;
            padding: 0 15px !important;
            min-height: 150px !important;
            height: 150px !important;
            display: flex !important;
            flex-direction: column !important;
            box-sizing: border-box !important;
          }
          
          /* Remove focus outline from all header interactive elements */
          .header a:focus,
          .header a:focus-visible,
          .header button:focus,
          .header button:focus-visible,
          .header a:active,
          .header button:active {
            outline: none !important;
            box-shadow: none !important;
          }
          
          .header > .container {
            height: 100% !important;
            max-width: ${siteSettings?.header_full_width ? '100%' : `${siteSettings?.header_max_width || 1400}px`} !important;
            margin: 0 auto !important;
            padding: 0 15px !important;
          }
          
          .header .header-content {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            height: 100% !important;
            min-height: 150px !important;
            padding: 0 !important;
          }
          
          .nav {
            display: flex !important;
            align-items: center !important;
            gap: ${siteSettings?.nav_link_spacing || 20}px !important;
            padding: 0 !important;
          }
          
          .nav a {
            color: var(--nav-link-color, #333) !important;
            text-decoration: none !important;
            padding: ${siteSettings?.nav_link_padding || 10}px !important;
          }
          
          .logo {
            margin: 0 !important;
            padding: 0 !important;
            outline: none !important;
            text-decoration: none !important;
          }
          
          .logo:focus,
          .logo:focus-visible,
          .logo:active {
            outline: none !important;
            box-shadow: none !important;
          }
          
          .logo-container, .header-actions {
            display: flex !important;
            align-items: center !important;
          }
          
          /* Default: hide mobile nav on desktop */
          .nav-mobile {
            display: none !important;
            align-items: center;
            gap: 12px;
          }
          
          /* Hamburger menu button styling */
          .hamburger-menu {
            display: flex;
            flex-direction: column;
            gap: 4px;
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
          }
          
          .hamburger-menu span {
            width: 25px;
            height: 3px;
            background-color: var(--text-primary, #333);
            transition: all 0.3s;
            display: block;
          }
          
          @media (max-width: 999px) {
            .main-nav { display: none !important; }
            .nav-mobile { display: flex !important; align-items: center; gap: 12px; }
            .hide-mobile { display: none !important; }
          }
          
          @media (min-width: 1000px) {
            .main-nav { display: flex !important; }
            .nav-mobile { display: none !important; }
            .hide-mobile { display: flex !important; }
          }
        `}</style>
        <div className="container">
          <div className="header-content">
            {/* Logo */}
            <div className="logo-section">
              <Link to="/" className="logo-link" onClick={closeMobileMenu}>
                <h1 className="logo-title"></h1>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="main-nav nav-menu" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/products" className="nav-link">Products</Link>
              
              {/* Categories Dropdown */}
              {categories.length > 0 && (
                <div 
                  className="categories-dropdown"
                  onMouseEnter={() => setShowCategoriesDropdown(true)}
                  onMouseLeave={() => setShowCategoriesDropdown(false)}
                >
                  <span className="dropdown-trigger">Categories ▼</span>
                  {showCategoriesDropdown && (
                    <div className="dropdown-menu">
                      {categories.slice(0, 8).map(category => (
                        <Link
                          key={category.id}
                          to={`/category/${category.slug}`}
                          className="dropdown-item"
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Mobile: Cart + Menu Button - visibility controlled by CSS media queries */}
            <div className="nav-mobile">
              {/* Mobile Cart Icon */}
              <Link 
                to="/cart" 
                onClick={closeMobileMenu}
                style={{
                  position: 'relative',
                  fontSize: '24px',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <span>🛒</span>
                {getTotalItems() > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-10px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {getTotalItems()}
                  </span>
                )}
              </Link>
              
              {/* Hamburger Menu Button */}
              <button 
                className={`hamburger-menu ${mobileMenuOpen ? 'active' : ''}`}
                onClick={toggleMobileMenu}
                aria-label="Toggle mobile menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>

            {/* Right Section */}
            <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {/* Cart */}
              <Link to="/cart" className="cart-link" onClick={closeMobileMenu} style={{ position: 'relative' }}>
                🛒
                {getTotalItems() > 0 && (
                  <span className="cart-badge" style={{ 
                    position: 'absolute', 
                    top: '-8px', 
                    right: '-8px', 
                    backgroundColor: 'red', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '18px', 
                    height: '18px', 
                    fontSize: '12px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}>
                    {getTotalItems()}
                  </span>
                )}
              </Link>

              {/* Auth Section - Desktop Only */}
              <div className="hide-mobile">
                {isAuthenticated ? (
                  <div className="auth-links" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Link to="/profile" className="nav-link">
                      {user?.first_name || 'Profile'}
                    </Link>
                    {user?.is_admin && (
                      <Link to="/admin" className="nav-link">Admin</Link>
                    )}
                    <button onClick={handleLogout} className="logout-btn" style={{ 
                      padding: '5px 10px', 
                      backgroundColor: '#dc3545', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}>
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="auth-links" style={{ display: 'flex', gap: '10px' }}>
                    <Link to="/login" className="login-btn" style={{ 
                      padding: '5px 10px', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      textDecoration: 'none', 
                      borderRadius: '4px' 
                    }}>Login</Link>
                    <Link to="/register" className="register-btn" style={{ 
                      padding: '5px 10px', 
                      backgroundColor: '#28a745', 
                      color: 'white', 
                      textDecoration: 'none', 
                      borderRadius: '4px' 
                    }}>Register</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close mobile menu"
            >
              ✕
            </button>
            
            <ul className="mobile-menu-items">
              <li>
                <Link to="/" onClick={closeMobileMenu}>Home</Link>
              </li>
              <li>
                <Link to="/products" onClick={closeMobileMenu}>Products</Link>
              </li>
              
              {/* Categories in mobile menu */}
              {categories.slice(0, 8).map(category => (
                <li key={category.id}>
                  <Link 
                    to={`/category/${category.slug}`}
                    onClick={closeMobileMenu}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}

              {/* Auth links in mobile menu */}
              {isAuthenticated ? (
                <>
                  <li>
                    <Link to="/profile" onClick={closeMobileMenu}>
                      Profile ({user?.first_name || 'User'})
                    </Link>
                  </li>
                  {user?.is_admin && (
                    <li>
                      <Link to="/admin" onClick={closeMobileMenu}>Admin</Link>
                    </li>
                  )}
                  <li>
                    <button 
                      onClick={handleLogout} 
                      className="mobile-logout-btn"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-primary, #333)',
                        fontSize: '18px',
                        padding: '15px 0',
                        width: '100%',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--border-color, #eee)'
                      }}
                    >
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link to="/login" onClick={closeMobileMenu}>Login</Link>
                  </li>
                  <li>
                    <Link to="/register" onClick={closeMobileMenu}>Register</Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}

        {/* Mobile responsive styles */}
      </header>
    );
  }

  // Main header with site settings - NO INLINE STYLES
  return (
    <header 
      ref={headerRef}
      className={`header ${siteSettings?.header_sticky ? 'sticky' : ''}`}
    >
      {/* Marquee animations - Admin settings priority */}
      <style jsx="true">{`
        /* Marquee Animations - Controlled by Admin Settings */
        /* Left = Right to Left movement (normal marquee) */
        @keyframes marquee-scroll-left {
          0% { transform: translateX(calc(100vw + 100px)); }
          100% { transform: translateX(calc(-100% - 100px)); }
        }
        
        /* Right = Left to Right movement (reverse marquee) */
        @keyframes marquee-scroll-right {
          0% { transform: translateX(calc(-100% - 100px)); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        
        .marquee-banner {
          overflow: hidden;
          white-space: nowrap;
          position: relative;
          width: 100%;
          max-width: 100vw;
          background-color: ${siteSettings?.marquee_background_color || 'transparent'} !important;
          color: ${siteSettings?.marquee_color || 'inherit'} !important;
          font-family: ${siteSettings?.marquee_font_family || 'inherit'} !important;
          font-size: ${siteSettings?.marquee_font_size || '14'}px !important;
        }
        
        .marquee-content {
          display: inline-block;
          font-family: ${siteSettings?.marquee_font_family || 'inherit'} !important;
          font-size: ${siteSettings?.marquee_font_size || '14'}px !important;
          padding-right: 50px;
          min-width: max-content;
        }
        
        .marquee-content.marquee-left {
          animation: marquee-scroll-left ${Math.max(5, 60 - (siteSettings?.marquee_speed || 30))}s linear infinite;
        }
        
        .marquee-content.marquee-right {
          animation: marquee-scroll-right ${Math.max(5, 60 - (siteSettings?.marquee_speed || 30))}s linear infinite;
        }
        
        .marquee-banner.pause-on-hover:hover .marquee-content {
          animation-play-state: paused;
        }
        
        /* Admin Settings Priority - Override any theme styles */
        .header {
          background-color: ${siteSettings?.header_background_color || 'var(--header-bg, transparent)'} !important;
          color: ${siteSettings?.header_text_color || 'var(--header-text, inherit)'} !important;
          border-bottom: ${siteSettings?.header_border_bottom ? `1px solid ${siteSettings?.header_border_color || '#e9ecef'}` : 'none'} !important;
          box-shadow: ${siteSettings?.header_shadow ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'} !important;
          padding-top: ${siteSettings?.header_padding || 15}px !important;
          padding-bottom: ${siteSettings?.header_padding || 15}px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          font-size: ${siteSettings?.header_font_size || 16}px !important;
          font-family: ${siteSettings?.header_font_family || 'Arial, sans-serif'} !important;
          height: ${(siteSettings?.header_height ?? 150)}px !important;
          min-height: ${(siteSettings?.header_height ?? 150)}px !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          box-sizing: border-box !important;
          width: 100% !important;
          max-width: 100vw !important;
          overflow-x: hidden !important;
          width: 100% !important;
        }

        /* Sticky Header */
        .header.sticky {
          position: sticky !important;
          top: 0 !important;
          z-index: 1000 !important;
        }

        /* Force header height on sticky and admin pages to override theme files */
        .header.sticky,
        .admin-page .header,
        .admin-page .header.sticky {
          height: ${(siteSettings?.header_height ?? 150)}px !important;
          min-height: ${(siteSettings?.header_height ?? 150)}px !important;
        }
        
        /* Container wrapper for centering */
        .header > .container-wrapper,
        .header .container-wrapper {
          flex: 1 !important;
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
        }
        
        /* Ensure container fills header height - Menu Settings Priority */
        .header > .container,
        .header .container,
        .header > .container-wrapper > .container,
        .header .container-wrapper .container {
          max-width: ${siteSettings?.header_full_width ? '100%' : `${siteSettings?.header_max_width || 1400}px`} !important;
          width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          padding: 0 ${siteSettings?.header_full_width ? '40px' : '20px'} !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          box-sizing: border-box !important;
        }
        
        .header .header-content,
        .header > .header-content {
          max-width: ${siteSettings?.header_full_width ? '100%' : `${siteSettings?.header_max_width || 1400}px`} !important;
          width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          padding: 0 ${siteSettings?.header_full_width ? '40px' : '20px'} !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          box-sizing: border-box !important;
        }
        
        /* Marquee should stay at top */
        .header .marquee-container,
        .header > .marquee-container {
          width: 100% !important;
          flex-shrink: 0 !important;
        }

        .nav {
          gap: ${siteSettings?.nav_link_spacing || 20}px !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        
        .nav a {
          color: ${siteSettings?.nav_link_color || 'var(--nav-link-color, inherit)'} !important;
          font-size: ${siteSettings?.nav_link_font_size || 14}px !important;
          font-weight: ${siteSettings?.nav_link_font_weight || '500'} !important;
          font-family: ${siteSettings?.nav_link_font_family || 'inherit'} !important;
          text-transform: ${siteSettings?.nav_link_text_transform || 'none'} !important;
          text-decoration: ${siteSettings?.nav_link_underline ? 'underline' : 'none'} !important;
          text-shadow: ${siteSettings?.nav_link_text_shadow 
            ? `${siteSettings?.nav_link_text_shadow_x || 1}px ${siteSettings?.nav_link_text_shadow_y || 1}px ${siteSettings?.nav_link_text_shadow_blur || 2}px ${siteSettings?.nav_link_text_shadow_color || 'rgba(0,0,0,0.3)'}` 
            : 'none'} !important;
          padding: ${siteSettings?.nav_link_padding || 10}px !important;
          transition: color 0.3s ease !important;
        }
        
        .nav a:hover {
          color: ${siteSettings?.nav_link_hover_color || 'var(--nav-link-hover-color, inherit)'} !important;
        }
        
        /* Logo and Site Title - with Effects */
        .site-title,
        .logo-title,
        .logo-link h1 {
          font-size: ${siteSettings?.header_font_size || 24}px !important;
          font-family: ${siteSettings?.header_font_family || 'Arial, sans-serif'} !important;
          font-weight: bold !important;
          margin: 0 !important;
          ${siteSettings?.logo_text_metallic ? `
            background: linear-gradient(180deg, #f0f0f0 0%, #c0c0c0 20%, #e0e0e0 40%, #a0a0a0 60%, #d0d0d0 80%, #b0b0b0 100%) !important;
            -webkit-background-clip: text !important;
            -webkit-text-fill-color: transparent !important;
            background-clip: text !important;
          ` : `
            color: ${siteSettings?.header_text_color || '#2c3e50'} !important;
          `}
          ${siteSettings?.logo_text_shadow && !siteSettings?.logo_text_metallic ? `
            text-shadow: ${siteSettings?.logo_text_shadow_x ?? 2}px ${siteSettings?.logo_text_shadow_y ?? 2}px ${siteSettings?.logo_text_shadow_blur ?? 4}px ${siteSettings?.logo_text_shadow_color || '#000000'} !important;
          ` : ''}
        }
        
        /* Shadow with metallic needs filter (text-shadow doesn't work with transparent text) */
        ${siteSettings?.logo_text_shadow && siteSettings?.logo_text_metallic ? `
        .logo-link {
          filter: drop-shadow(${siteSettings?.logo_text_shadow_x ?? 2}px ${siteSettings?.logo_text_shadow_y ?? 2}px ${Math.max(1, Math.floor((siteSettings?.logo_text_shadow_blur ?? 4) / 2))}px ${siteSettings?.logo_text_shadow_color || '#000000'}) !important;
        }
        ` : ''}

        .header .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 100% !important;
          min-height: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }

        /* Neutralize theme overrides that collapse header-content on admin pages */
        .admin-page .header-content {
          height: auto !important;
          min-height: ${(siteSettings?.header_height ?? 150)}px !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }

        /* Neutralize admin-page nav padding from theme files */
        .admin-page nav.nav,
        .admin-page nav.nav.nav-menu {
          padding: 0 !important;
        }

        /* Ensure logo and container have no extra spacing at top */
        .logo, .logo-container {
          margin: 0 !important;
          padding: 0 !important;
        }

        .logo-container, .header-actions {
          display: flex;
          align-items: center;
        }

        /* Show cart + hamburger when viewport < 1000px */
        @media (max-width: 999px) {
          .nav { display: none !important; }
          .nav-mobile { display: flex !important; align-items: center; gap: 12px; }
        }

        /* Show desktop nav when viewport >= 1000px */
        @media (min-width: 1000px) {
          .nav { display: flex !important; align-items: center; }
          .nav-mobile { display: none !important; }
        }
        
        /* Logo Settings - Admin Control */
        .site-logo {
          width: ${siteSettings?.logo_width || 120}px !important;
          height: ${siteSettings?.logo_height || 40}px !important;
          object-fit: contain !important;
          ${siteSettings?.logo_shadow_enabled ? `
            filter: drop-shadow(
              ${siteSettings?.logo_shadow_offset_x || 2}px 
              ${siteSettings?.logo_shadow_offset_y || 2}px 
              ${siteSettings?.logo_shadow_blur || 5}px 
              ${siteSettings?.logo_shadow_color || '#000000'}${Math.round((siteSettings?.logo_shadow_opacity || 0.3) * 255).toString(16).padStart(2, '0')}
            ) !important;
          ` : ''}
        }
        
        .site-logo2 {
          width: ${siteSettings?.logo2_width || 120}px !important;
          height: ${siteSettings?.logo2_height || 40}px !important;
          object-fit: contain !important;
          ${siteSettings?.logo2_shadow_enabled ? `
            filter: drop-shadow(
              ${siteSettings?.logo2_shadow_offset_x || 2}px 
              ${siteSettings?.logo2_shadow_offset_y || 2}px 
              ${siteSettings?.logo2_shadow_blur || 5}px 
              ${siteSettings?.logo2_shadow_color || '#000000'}${Math.round((siteSettings?.logo2_shadow_opacity || 0.3) * 255).toString(16).padStart(2, '0')}
            ) !important;
          ` : ''}
        }

        /* Mobile: force entire header + logo to fit viewport */
        @media (max-width: 768px) {
          /* Force header itself to viewport width */
          .header,
          .header.sticky {
            width: 100vw !important;
            max-width: 100vw !important;
            overflow: hidden !important;
            height: auto !important;
            min-height: 48px !important;
            max-height: 70px !important;
            padding-top: 5px !important;
            padding-bottom: 5px !important;
            box-sizing: border-box !important;
          }
          /* Force container and header-content to not exceed viewport */
          .header > .container,
          .header .container,
          .header > .container-wrapper > .container,
          .header .container-wrapper .container,
          .header .header-content,
          .header > .header-content {
            max-width: 100vw !important;
            width: 100% !important;
            padding: 0 10px !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          .header .header-content {
            min-height: 40px !important;
          }
          /* Logo images */
          .header .logo-container .site-logo,
          .header .logo-container .site-logo2,
          .site-logo,
          .site-logo2 {
            width: auto !important;
            max-width: min(35vw, 140px) !important;
            height: auto !important;
            max-height: 40px !important;
          }
          .logo-container {
            max-width: 40vw !important;
            overflow: hidden !important;
            gap: 4px !important;
            flex-shrink: 1 !important;
          }
          .logo-link,
          a.logo {
            max-width: 40vw !important;
            overflow: hidden !important;
            flex-shrink: 1 !important;
          }
          .logo-title,
          .site-title,
          .logo-link h1,
          a.logo h1 {
            font-size: 14px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 35vw !important;
          }
          /* Marquee */
          .marquee-banner {
            max-width: 100vw !important;
            overflow: hidden !important;
          }
        }

        @media (max-width: 480px) {
          .header,
          .header.sticky {
            max-height: 58px !important;
            min-height: 42px !important;
            padding-top: 3px !important;
            padding-bottom: 3px !important;
          }
          .header > .container,
          .header .container,
          .header .header-content,
          .header > .header-content {
            padding: 0 8px !important;
          }
          .header .logo-container .site-logo,
          .header .logo-container .site-logo2,
          .site-logo,
          .site-logo2 {
            width: auto !important;
            max-width: min(28vw, 100px) !important;
            max-height: 32px !important;
          }
          .logo-container {
            max-width: 32vw !important;
            gap: 2px !important;
          }
          .logo-link,
          a.logo {
            max-width: 32vw !important;
          }
          .logo-title,
          .site-title,
          .logo-link h1,
          a.logo h1 {
            font-size: 12px !important;
            max-width: 28vw !important;
          }
        }
      `}</style>
      
      {/* Marquee Banner */}
      {siteSettings?.marquee_enabled && (
        <div 
          className={`marquee-banner ${siteSettings?.marquee_pause_on_hover ? 'pause-on-hover' : ''}`}
        >
          <div 
            className={`marquee-content ${siteSettings?.marquee_direction === 'right' ? 'marquee-right' : 'marquee-left'}`}
          >
            <span>{siteSettings?.marquee_text}</span>
          </div>
        </div>
      )}
      
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo">
            <div className="logo-container">
              {/* Logo order controlled by logo2_first setting */}
              {siteSettings?.logo2_first ? (
                <>
                  {/* Logo 2 first */}
                  {siteSettings?.use_logo2 && siteSettings?.site_logo2 && (
                    <img 
                      src={createApiUrl(siteSettings?.site_logo2.startsWith('/') ? siteSettings?.site_logo2.slice(1) : siteSettings?.site_logo2)} 
                      alt={`${siteSettings?.site_name || ''} Logo 2`} 
                      className="site-logo site-logo2"
                    />
                  )}
                  {/* Then Logo 1 or Site Name */}
                  {siteSettings?.use_logo && siteSettings?.site_logo ? (
                    <img 
                      src={createApiUrl(siteSettings?.site_logo.startsWith('/') ? siteSettings?.site_logo.slice(1) : siteSettings?.site_logo)} 
                      alt={siteSettings?.site_name || ''} 
                      className="site-logo"
                    />
                  ) : (
                    <h1 className="site-title">{siteSettings?.site_name || ''}</h1>
                  )}
                </>
              ) : (
                <>
                  {/* Default order: Logo 1 or Site Name first */}
                  {siteSettings?.use_logo && siteSettings?.site_logo ? (
                    <img 
                      src={createApiUrl(siteSettings?.site_logo.startsWith('/') ? siteSettings?.site_logo.slice(1) : siteSettings?.site_logo)} 
                      alt={siteSettings?.site_name || ''} 
                      className="site-logo"
                    />
                  ) : (
                    <h1 className="site-title">{siteSettings?.site_name || ''}</h1>
                  )}
                  {/* Then Logo 2 */}
                  {siteSettings?.use_logo2 && siteSettings?.site_logo2 && (
                    <img 
                      src={createApiUrl(siteSettings?.site_logo2.startsWith('/') ? siteSettings?.site_logo2.slice(1) : siteSettings?.site_logo2)} 
                      alt={`${siteSettings?.site_name || ''} Logo 2`} 
                      className="site-logo site-logo2"
                    />
                  )}
                </>
              )}
            </div>
          </Link>
          
          {/* Mobile: Cart + Menu Button - visibility controlled by CSS media queries */}
          <div className="nav-mobile">
            {/* Mobile Cart Icon - Always visible on mobile */}
            <Link 
              to="/cart" 
              className="mobile-cart-icon"
              onClick={closeMobileMenu}
              style={{
                position: 'relative',
                fontSize: '24px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span>🛒</span>
              {getTotalItems() > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-10px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {getTotalItems()}
                </span>
              )}
            </Link>
            
            {/* Hamburger Menu Button */}
            <button 
              className={`hamburger-menu ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="nav nav-menu">
            {siteSettings?.navigation_links && siteSettings?.navigation_links
              .filter(link => link.enabled)
              .sort((a, b) => a.order - b.order)
              .filter(link => {
                if (link.show_for === 'guest' && isAuthenticated) return false;
                if (link.show_for === 'user' && !isAuthenticated) return false;
                if (link.show_for === 'admin' && (!isAuthenticated || !user?.is_admin)) return false;
                return true;
              })
              .map(link => {
                if (link.url === 'logout') {
                  return (
                    <button 
                      key={link.id} 
                      onClick={handleLogout} 
                      className="nav-logout-btn"
                    >
                      {link.title}
                    </button>
                  );
                }
                
                if (link.url === '/products' && categories.length > 0) {
                  return (
                    <div 
                      key={link.id} 
                      className="nav-dropdown"
                      onMouseEnter={() => setShowCategoriesDropdown(true)}
                      onMouseLeave={() => setShowCategoriesDropdown(false)}
                    >
                      <Link to={link.url} className="nav-link">
                        {link.title} ({categories.filter(cat => cat.is_active).length})
                      </Link>
                      
                      {showCategoriesDropdown && (
                        <div className="dropdown-menu">
                          <Link to="/products" className="dropdown-item dropdown-header">
                            All Categories
                          </Link>
                          {categories.filter(cat => cat.is_active).map(category => (
                            <Link 
                              key={category.id}
                              to={`/products?category=${category.slug}`}
                              className="dropdown-item"
                            >
                              {category.name}
                              {category.product_count !== undefined && (
                                <span className="product-count">{category.product_count}</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                
                if (link.is_internal) {
                  return (
                    <Link key={link.id} to={link.url} className="nav-link">
                      {link.title}
                    </Link>
                  );
                } else {
                  return (
                    <a 
                      key={link.id} 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="nav-link"
                    >
                      {link.title}
                    </a>
                  );
                }
              })}
              
            {/* Cart Icon */}
            <Link to="/cart" className="cart-icon">
              <span className="cart-emoji">🛒</span>
              {getTotalItems() > 0 && (
                <span className="cart-count">{getTotalItems()}</span>
              )}
            </Link>

            {/* User Icon */}
            {isAuthenticated ? (
              <Link 
                to={user?.is_admin ? "/admin" : "/user-dashboard"} 
                className="user-icon"
                title={`Welcome, ${user?.first_name || 'User'}!`}
              >
                <span className="user-emoji">👤</span>
              </Link>
            ) : (
              <Link 
                to="/login" 
                className="login-icon"
                title="Login"
              >
                <span className="login-emoji">🔑</span>
              </Link>
            )}
          </nav>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <button 
            className="mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Close mobile menu"
          >
            ✕
          </button>
          
          <ul className="mobile-menu-items">
            {/* Navigation Links for Mobile */}
            {siteSettings?.navigation_links && siteSettings?.navigation_links
              .filter(link => link.enabled)
              .sort((a, b) => a.order - b.order)
              .filter(link => {
                if (link.show_for === 'guest' && isAuthenticated) return false;
                if (link.show_for === 'user' && !isAuthenticated) return false;
                if (link.show_for === 'admin' && (!isAuthenticated || !user?.is_admin)) return false;
                return true;
              })
              .map(link => {
                if (link.url === 'logout') {
                  return (
                    <li key={link.id}>
                      <button 
                        onClick={handleLogout} 
                        className="mobile-logout-btn"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-primary, #333)',
                          fontSize: '18px',
                          padding: '15px 0',
                          width: '100%',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--border-color, #eee)'
                        }}
                      >
                        {link.title}
                      </button>
                    </li>
                  );
                }
                
                if (link.url === '/products' && categories.length > 0) {
                  return [
                    <li key={link.id}>
                      <Link to={link.url} onClick={closeMobileMenu}>
                        {link.title} ({categories.filter(cat => cat.is_active).length})
                      </Link>
                    </li>,
                    ...categories.filter(cat => cat.is_active).slice(0, 8).map(category => (
                      <li key={`mobile-cat-${category.id}`} style={{paddingLeft: '20px'}}>
                        <Link 
                          to={`/products?category=${category.slug}`}
                          onClick={closeMobileMenu}
                          style={{ fontSize: '16px', color: 'var(--text-secondary, #666)' }}
                        >
                          {category.name}
                        </Link>
                      </li>
                    ))
                  ];
                }
                
                if (link.is_internal) {
                  return (
                    <li key={link.id}>
                      <Link to={link.url} onClick={closeMobileMenu}>
                        {link.title}
                      </Link>
                    </li>
                  );
                } else {
                  return (
                    <li key={link.id}>
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={closeMobileMenu}
                      >
                        {link.title}
                      </a>
                    </li>
                  );
                }
              }).flat()}

            {/* User Actions in Mobile Menu */}
            {isAuthenticated ? (
              <li>
                <Link 
                  to={user?.is_admin ? "/admin" : "/user-dashboard"} 
                  onClick={closeMobileMenu}
                >
                  👤 Profile ({user?.first_name || 'User'})
                </Link>
              </li>
            ) : (
              <li>
                <Link to="/login" onClick={closeMobileMenu}>
                  🔑 Login
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
});

export default Header;
